<template>
  <BGModal :shown="dialogShown"
           :title="$t('admin_promotion.edit_title')"
           fixed-tabs>
    <template #content>
      <v-container class="edit-promotion-content" fluid v-if="promotion">
        <v-tabs fixed-tabs
                v-model="tabIndex">
          <v-tab>{{ $t('register.basic_info') }}</v-tab>
          <v-tab-item>
            <v-container fluid>
              <v-form ref="promotionForm">
                <v-row>
                  <v-col>
                    <v-text-field outlined
                                  :label="$t('admin_promotion.name')"
                                  v-model="promotion.name"
                                  clearable
                                  :rules="requiredRules"></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-input :rules="requiredRules"
                             hide-details="auto"
                             v-model="promotion.from_time">
                      <v-container fluid class="flex-column">
                        <legend class="v-label theme--light">{{ $t('admin_promotion.from_time') }}</legend>
                        <DateTimePickerInput v-model="promotion.from_time"
                                             hide-details></DateTimePickerInput>
                      </v-container>
                    </v-input>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-input :rules="requiredRules"
                             hide-details="auto"
                             v-model="promotion.to_time">
                      <v-container fluid class="flex-column">
                        <legend class="v-label theme--light">{{ $t('admin_promotion.to_time') }}</legend>
                        <DateTimePickerInput v-model="promotion.to_time"
                                             hide-details></DateTimePickerInput>
                      </v-container>
                    </v-input>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-input hide-details="auto"
                             v-model="promotion.icon"
                             :rules="requiredRules">
                      <v-container fluid class="flex-column">
                        <legend class="v-label theme--light">{{ $t('admin_promotion.icon') }}</legend>
                        <PhotoPreviewInput v-model="promotion.icon"
                                           div-class="promotion-icon-input"
                                           file-name="promotion-icon"></PhotoPreviewInput>
                        <small class="form-text text-muted">ACCEPTED FILE FORMAT: PNG, JPEG, JPG; RECOMMENDED SIZE: <b>335(W) X 335(H)</b></small>
                      </v-container>
                    </v-input>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-input :rules="requiredRules"
                             hide-details="auto"
                             v-model="promotion.user_action">
                      <v-container fluid class="flex-column">
                        <legend class="v-label theme--light">{{ $t('admin_promotion.user_action') }}</legend>
                        <v-radio-group v-model="promotion.user_action" hide-details mandatory>
                          <v-radio :label="$t('admin_promotion.user_action_download_attachment')"
                                   value="download_attachment"></v-radio>
                          <v-radio :label="$t('admin_promotion.user_action_open_attachment')"
                                   value="open_attachment"></v-radio>
                          <v-radio :label="$t('admin_promotion.user_action_external_url')"
                                   value="external_url"></v-radio>
                        </v-radio-group>
                      </v-container>
                    </v-input>
                  </v-col>
                </v-row>
                <v-row v-if="promotion.user_action === 'external_url'">
                  <v-col>
                    <v-text-field outlined
                                  :label="$t('admin_promotion.external_url')"
                                  v-model="promotion.external_url"
                                  clearable
                                  :rules="requiredRules"></v-text-field>
                  </v-col>
                </v-row>
                <v-row v-else>
                  <v-col>
                    <v-file-input :label="$t('admin_promotion.content')"
                                  accept="image/jpeg, image/png, image/jpg, application/pdf"
                                  hint="ACCEPTED FILE FORMAT: PNG, JPEG, JPG, PDF"
                                  persistent-hint
                                  clearable
                                  v-model="promotion.content"
                                  :rules="requiredRules">
                      <template #selection="{file, text}">
                        {{ text || file }}
                      </template>
                      <template #append-outer>
                        <a :href="getURL(promotion.content)"
                           target="_blank"
                           v-if="promotion.content">
                          <v-icon>fas fa-search</v-icon>
                        </a>
                      </template>
                    </v-file-input>
                  </v-col>
                </v-row>
              </v-form>
            </v-container>
          </v-tab-item>

          <v-tab>{{ $t('admin_hkiarace.email_template') }}</v-tab>
          <v-tab-item>
            <v-container fluid>
              <v-row>
                <v-col>
                  <v-text-field outlined
                                :rules="requiredRules"
                                :label="$t('admin_notification_template.email_subject')"
                                v-model="template.email_subject"></v-text-field>
                </v-col>
              </v-row>
              <v-row class="pt-0">
                <v-col>
                  <v-input :rules="requiredRules"
                           hide-details="auto"
                           v-model="template.email_content">
                    <v-container fluid class="flex-column">
                      <legend class="v-label theme--light">{{ $t('admin_notification_template.email_content') }}</legend>
                      <TinyMceEditor v-model="template.email_content"></TinyMceEditor>
                    </v-container>
                  </v-input>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-checkbox :label="$t('admin_notification_template.push_notification_enabled')"
                              v-model="template.push_notification_enabled">
                  </v-checkbox>
                </v-col>
              </v-row>
              <v-row v-if="template.push_notification_enabled">
                <v-col>
                  <v-text-field outlined
                                :rules="requiredRules"
                                :label="$t('admin_notification_template.push_notification_content')"
                                v-model="template.push_notification_content"></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field outlined
                                :label="$t('admin_notification_template.test_email')"
                                :placeholder="$t('admin_notification_template.recipient')"
                                v-model="testEmailRecipient">
                    <template #append-outer>
                      <v-tooltip top>
                        <template #activator="{on, attrs}">
                          <v-btn icon
                                 v-on="on"
                                 v-bind="attrs"
                                 @click="sendTestEmail"
                                 :disabled="!testEmailRecipient">
                            <v-icon>fas fa-paper-plane</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('admin_notification_template.send_email') }}</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>
        </v-tabs>

      </v-container>
    </template>
    <template #footer>
      <v-row no-gutters>
        <v-btn @click="cancel"
               class="mr-3"
               depressed
               large>
          {{ $t('cancel') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary"
               depressed
               large
               v-if="tabIndex === 0"
               @click="$refs.promotionForm.validate() && savePromotion()">
          {{ $t('save') }}
          <v-icon right>fas fa-check</v-icon>
        </v-btn>
        <v-btn color="primary"
               depressed
               large
               v-if="tabIndex === 1"
               :disabled="!templateChanged"
               @click="saveNotificationTemplate">
          {{ $t('save') }}
          <v-icon right>fas fa-check</v-icon>
        </v-btn>
      </v-row>
    </template>
  </BGModal>
</template>
<script>
import BGModal from "@/components/BGModal";
import {computed, ref, watch} from "@vue/composition-api";
import PhotoPreviewInput from "@/components/PhotoPreviewInput";
import DateTimePickerInput from "@/components/DateTimePickerInput";
import TinyMceEditor from "@/components/TinyMceEditor";
import _ from 'lodash';

export default {
  name: 'EditPromotionModal',
  components: {TinyMceEditor, DateTimePickerInput, PhotoPreviewInput, BGModal},
  props: {
    shown: Boolean,
    promotionId: String,
  },
  setup(props, {root, emit}) {
    const dialogShown = ref(props.shown);

    const tabIndex = ref(0);

    // Init
    watch(() => props.shown, async (newValue, oldValue) => {
      if (newValue !== oldValue) {
        dialogShown.value = newValue;

        if (newValue) {
          tabIndex.value = 0;
          getPromotion();
          getNotificationTemplate();
        }
      }

      tabIndex.value = 0;
    });
    const cancel = function () {
      emit('cancel');
    };
    const getPromotion = function () {
      root.$callAPI('hkiarace/admin/promotion/', {id: props.promotionId}, function (response) {
        promotion.value = response.body.promotion;
      });
    }

    const getURL = function (f) {
      if (f instanceof File) {
        return URL.createObjectURL(f);
      } else {
        return 'api/static/' + f;
      }
    };

    // Info
    const promotion = ref(null);
    const requiredRules = ref([
      value => !!value || root.$t('register.required_fields'),
    ]);
    const savePromotion = async function () {
      const isExternalUrl = promotion.value.user_action === 'external_url';
      root.$callAPI('hkiarace/admin/promotion/edit/', {
        ...promotion.value,

        icon: promotion.value.icon instanceof File ? await root.$convertFileToBase64(promotion.value.icon) : promotion.value.icon,
        content: isExternalUrl ? null : (promotion.value.content instanceof File ? await root.$convertFileToBase64(promotion.value.content) : promotion.value.content),
        external_url: isExternalUrl ? promotion.value.external_url : null,
      }, () => {
        root.$showAlertMessage('Info Saved');
        getPromotion();
      })
    }

    // Email Template
    const template = ref(null);
    const originalTemplate = ref(null);
    const templateChanged = computed(() => {
      return !_.isEqual(template.value, originalTemplate.value);
    })
    const getNotificationTemplate = function () {
      root.$callAPI('hkiarace/admin/promotion/email_template/', {
        promotion_id: props.promotionId,
      }, function (response) {
        template.value = {
          name: '',
          email_subject: '',
          email_content: '',
          push_notification_enabled: '',
          push_notification_content: '',
          ...response.body.template,
        };
        originalTemplate.value = {...template.value};
      });
    };
    const testEmailRecipient = ref(null);
    const sendTestEmail = function () {
      root.$callAPI('event/email_template/test/', {
        subject: template.value.email_subject,
        content: template.value.email_content,
        recipient: testEmailRecipient.value,
      }, function (response) {
        root.$showAlertMessage('Email Sent');
      })
    };
    const saveNotificationTemplate = function () {
      if (templateChanged.value) {
        this.$callAPI('hkiarace/admin/promotion/email_template/set/', {
          ...template.value,
          promotion_id: props.promotionId,
        }, function (response) {
          root.$showAlertMessage('Template Saved');
          getNotificationTemplate();
        })
      }
    };

    return {
      dialogShown,
      tabIndex,

      cancel,

      getURL,

      // Info
      promotion,
      requiredRules,
      savePromotion,

      // Email Template
      template,
      originalTemplate,
      templateChanged,
      testEmailRecipient,
      sendTestEmail,
      saveNotificationTemplate,
    }
  }
}
</script>

<style lang="less">
.edit-promotion-content {
  display: flex;
  height: 100%;
  flex-direction: column;
}
</style>
